/**
 * 入金の照合(銀行の入金 → 請求書の消込・2026-10-23 段階3)。全体設計 5章「入金消込: 第1段は手動+銀行CSV」。
 *
 *  - 銀行の入金 = 入出金(cash_entries)の「入金・確定済み」の行(CSV取込・手入力)。資金移動は除く
 *  - 候補の決め方: **金額が請求書の残高とちょうど同じ** かつ 入金日が「発行日 〜 入金予定日の60日後」の間
 *    (振込人名はカタカナ・略称が多く名前では当てにくいため、第1版は金額と日付で当てる)
 *  - 1件の入金を2枚の請求書に使わない(すでに請求書の入金記録に使った入金は除く)
 *  - 1対1で決まるもの(その請求書の候補が1件だけで、その入金も1枚の請求書にしか当たらない)を「消込の候補」として出す。
 *    書き込むのは人が「消し込む」を押したときだけ
 *  - 当たらないもの(手数料を引かれた・まとめて振り込まれた等)は、これまでどおり「入金を記録」で手入力する
 *
 * 具体例は tests/unit/receiptMatching.test.ts
 */
import { addDays } from '@core/lib/date';
import type { CashEntry, DateStr, Invoice } from '@core/lib/types';
import { balanceOf } from './invoice';

/** 入金予定日から何日後までの入金を候補にするか */
export const LATE_DAYS = 60;

/** 請求書の入金記録に使った入金の ID */
export function usedCashEntryIds(invoices: Pick<Invoice, 'receipts'>[]): Set<string> {
  return new Set(invoices.flatMap((i) => i.receipts.map((r) => r.cash_entry_id).filter((x): x is string => !!x)));
}

/**
 * 照合に使える入金(入金・確定済み・資金移動でない・取引に紐づいていない・まだ使っていない)。
 * 「入金を記録」で手入力した入金(どの銀行の入金か分からない)と同じ日・同じ金額の入金は、使ったものとみなして出さない
 */
export function availableDeposits(entries: CashEntry[], used: Set<string>, invoices: Pick<Invoice, 'receipts'>[] = []): CashEntry[] {
  const manual = new Map<string, number>();
  for (const i of invoices) {
    for (const r of i.receipts) if (!r.cash_entry_id && r.amount > 0) manual.set(`${r.date}|${r.amount}`, (manual.get(`${r.date}|${r.amount}`) ?? 0) + 1);
  }
  const out: CashEntry[] = [];
  for (const e of entries) {
    if (e.direction !== 'in' || e.status !== 'settled' || e.kind === 'transfer' || e.transaction_id || used.has(e.id)) continue;
    const k = `${e.date}|${e.amount}`;
    const left = manual.get(k) ?? 0;
    if (left > 0) {
      manual.set(k, left - 1);
      continue;
    }
    out.push(e);
  }
  return out;
}

/** 請求書1枚の候補(金額一致・期間内)。入金予定日に近い順 */
export function candidatesFor(invoice: Invoice, deposits: CashEntry[]): CashEntry[] {
  const balance = balanceOf(invoice);
  if (balance <= 0 || invoice.status === 'void') return [];
  const until = addDays(invoice.due_date, LATE_DAYS);
  const dist = (d: DateStr) => Math.abs(new Date(`${d}T00:00:00Z`).getTime() - new Date(`${invoice.due_date}T00:00:00Z`).getTime());
  return deposits
    .filter((e) => e.amount === balance && e.date >= invoice.issue_date && e.date <= until)
    .sort((a, b) => dist(a.date) - dist(b.date) || (a.id < b.id ? -1 : 1));
}

export type MatchProposal = { invoice: Invoice; deposit: CashEntry };

/**
 * 1対1で決まる組み合わせ。候補が2件以上の請求書・2枚以上に当たる入金は出さない(人が「入金を記録」で選ぶ)
 */
export function proposeMatches(openInvoices: Invoice[], deposits: CashEntry[]): {
  proposals: MatchProposal[];
  /** 候補が複数あって決められない請求書の数 */
  ambiguous: number;
} {
  const byInvoice = openInvoices.map((invoice) => ({ invoice, cands: candidatesFor(invoice, deposits) }));
  const hits = new Map<string, number>();
  for (const { cands } of byInvoice) for (const c of cands) hits.set(c.id, (hits.get(c.id) ?? 0) + 1);
  const proposals: MatchProposal[] = [];
  let ambiguous = 0;
  for (const { invoice, cands } of byInvoice) {
    if (cands.length === 0) continue;
    if (cands.length === 1 && hits.get(cands[0]!.id) === 1) proposals.push({ invoice, deposit: cands[0]! });
    else ambiguous++;
  }
  proposals.sort((a, b) => (a.deposit.date < b.deposit.date ? -1 : a.deposit.date > b.deposit.date ? 1 : 0));
  return { proposals, ambiguous };
}
